/**
 * The settings screen's decisions, separated from its markup.
 *
 * Everything here is a fact about the settings collection — which keys exist,
 * what their fields mean, which of them anything actually reads, how a stored
 * value becomes an editable draft and back. It lives apart from the page so it
 * can be tested without a server, a router or a DOM, and so that a claim the
 * interface makes to an operator ("nothing reads this") is written down once,
 * with its evidence, rather than restated in JSX.
 */

import type { MessageKey } from '@/i18n'

/* The keys the collection holds. Constants for the same reason they are
   constants in Go: a typo here reads as "the server does not hold this
   setting", which is indistinguishable from the truth. */
export const KEY_AUTONOMY = 'autonomy.default'
export const KEY_ROUTING_WEIGHTS = 'routing.weights'
export const KEY_RETENTION = 'retention'
export const KEY_SETUP = 'setup'
/** The chief's designation: which account Kimene's own judgement is spent on
 *  (ADR-0021, ADR-0025). The Go constant is `seed.SettingSupervisor`, and the
 *  key keeps that name because it is what the stored document and the runbooks
 *  already say — renaming released state to match a screen's vocabulary buys
 *  nothing. */
export const KEY_SUPERVISOR = 'supervisor'
/** The controls an operator turns on rather than ones Kimene assumes
 *  (ADR-0032). `seed.SettingSecurity` in Go. */
export const KEY_SECURITY = 'security'

/** The keys this page has a dedicated editor for. Everything else in the
 *  collection is shown read-only under "other" rather than hidden, so a key
 *  written by another part of the system is still visible to an operator. */
export const EDITED_KEYS: readonly string[] = [
  KEY_AUTONOMY,
  KEY_ROUTING_WEIGHTS,
  KEY_RETENTION,
  KEY_SECURITY,
  KEY_SUPERVISOR,
]

/**
 * Whether anything in this build reads a stored setting.
 *
 * `stored-only` means the document is persisted and versioned and audited, and
 * no component consults it: editing it changes nothing today, and what is in
 * force is the default compiled into the binary. That is not a defect of the
 * page — it is the state of the system — but a form that hides it invites an
 * operator to set a retention window, watch it save, and believe their audit
 * trail is now governed by it.
 *
 * Established by reading the consumers, not by assumption. `handlers_auth.go`
 * fetches `setup` on every session check; two sweeps read `retention` (below);
 * nothing reads `autonomy.default`; and `docs/ROUTING.md` records the routing
 * weights as inert under its own known-gaps table — the engine scores from
 * `DefaultWeights()`, whose component names are not even the ones seeded here.
 *
 * **When a reader appears, delete its key from this table in the same change.**
 * A stale entry here is a lie in the opposite direction, and the interface's
 * whole claim rests on this table being maintained.
 */
export type Enforcement = 'read' | 'stored-only' | 'unknown'

const STORED_ONLY = new Set<string>([KEY_AUTONOMY, KEY_ROUTING_WEIGHTS])
// `supervisor` is read: `handlers_supervisor.go` resolves the designated account
// from it for a decision turn, and since ADR-0025 `handlers_chief.go` resolves
// the same designation for every next-step analysis the board asks for. It was
// never in the stored-only set, and it must never be added to one — the whole
// claim this table makes rests on it saying what is true today.
//
// `retention` moved here on 2026-10-17, and the move is what the withdrawal of
// `auditDays` was for. It had been called stored-only while
// `cmd/kimene-server` deleted raw recordings on `terminalRecordingDays` every
// hour and `cmd/kimene-worker` purged the event archive on `domainEventDays` —
// the screen printing "nothing reads this" over two windows that really do
// delete, which is the lie in the direction this table exists to prevent. It
// was left stale rather than patched because a single verdict per key could not
// be made true while a third window governed nothing: `auditDays` was inert for
// its whole life. With it gone, every field of this document has a reader and
// one verdict is honest again. Adding a window that nothing reads puts the key
// back in the set above — or, better, does not add it.
const READ = new Set<string>([KEY_SETUP, KEY_SUPERVISOR, KEY_RETENTION, KEY_SECURITY])

export function enforcementOf(key: string): Enforcement {
  if (STORED_ONLY.has(key)) return 'stored-only'
  if (READ.has(key)) return 'read'
  // A key this page has never heard of. Kimene does not render unmeasured as
  // measured, and it does not render unexamined as unread either.
  return 'unknown'
}

/* ---------------------------------------------------------------- Shapes */

/** Narrows a setting value to a plain object. Anything else — a string, an
 *  array, null — is treated as "not the shape this editor reads" rather than
 *  spread into one, because spreading a string produces indexed characters and
 *  a form full of nonsense. */
export function asObject(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {}
  return value as Record<string, unknown>
}

export function numberFieldsOf(value: unknown): Record<string, number> {
  const out: Record<string, number> = {}
  for (const [k, v] of Object.entries(asObject(value))) {
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v
  }
  return out
}

export function stringFieldsOf(value: unknown): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(asObject(value))) {
    if (typeof v === 'string') out[k] = v
  }
  return out
}

type Flat = Record<string, string | number>

/** The fields whose draft differs from what the server holds. Drives both the
 *  per-row "changed" marks and the section's dirty state, so a row can never be
 *  marked changed while the Save button says there is nothing to save. */
export function changedKeys(draft: Flat, stored: Flat): Set<string> {
  const out = new Set<string>()
  for (const k of new Set([...Object.keys(draft), ...Object.keys(stored)])) {
    if (draft[k] !== stored[k]) out.add(k)
  }
  return out
}

export function isDirty(draft: Flat, stored: Flat): boolean {
  return changedKeys(draft, stored).size > 0
}

/* -------------------------------------------------------------- Autonomy */

export const AUTONOMY_FIELDS = [
  { field: 'isolatedWrites', label: 'settings.autonomyIsolatedWrites' },
  { field: 'mainMerge', label: 'settings.autonomyMainMerge' },
  { field: 'productionDeploy', label: 'settings.autonomyProductionDeploy' },
  { field: 'destructiveAction', label: 'settings.autonomyDestructiveAction' },
  { field: 'externalPublish', label: 'settings.autonomyExternalPublish' },
  { field: 'financialSpend', label: 'settings.autonomyFinancialSpend' },
] as const satisfies ReadonlyArray<{ field: string; label: MessageKey }>

export type AutonomyField = (typeof AUTONOMY_FIELDS)[number]['field']

/** What each class of consequential action covers. The names alone are not
 *  self-explanatory — "isolated writes" and "destructive action" are Kimene's
 *  vocabulary, not everyone's — and a policy nobody understands is a policy
 *  nobody sets deliberately. Keyed by the field union rather than by string, so
 *  a class added above without an explanation does not compile. */
export const AUTONOMY_HINT: Record<AutonomyField, MessageKey> = {
  isolatedWrites: 'settings.autonomyIsolatedWritesHint',
  mainMerge: 'settings.autonomyMainMergeHint',
  productionDeploy: 'settings.autonomyProductionDeployHint',
  destructiveAction: 'settings.autonomyDestructiveActionHint',
  externalPublish: 'settings.autonomyExternalPublishHint',
  financialSpend: 'settings.autonomyFinancialSpendHint',
}

/** The three decisions domain.AutonomyPolicy's fields take. */
export const DECISIONS = ['autonomous', 'require_approval', 'forbidden'] as const
export type Decision = (typeof DECISIONS)[number]

export const DECISION_LABEL: Record<Decision, MessageKey> = {
  autonomous: 'settings.decisionAutonomous',
  require_approval: 'settings.decisionRequireApproval',
  forbidden: 'settings.decisionForbidden',
}

export function isKnownDecision(value: string): value is Decision {
  return (DECISIONS as readonly string[]).includes(value)
}

/* --------------------------------------------------------------- Routing */

/** Human names for the routing components the seeded document carries. A key
 *  with no entry keeps its raw name — inventing a friendly label for a
 *  component nobody has named would be guessing at what it scores. */
export const ROUTING_LABEL: Record<string, MessageKey> = {
  account_preference: 'settings.weightAccountPreference',
  capacity_headroom: 'settings.weightCapacityHeadroom',
  historical_success: 'settings.weightHistoricalSuccess',
  host_locality: 'settings.weightHostLocality',
  latency: 'settings.weightLatency',
  model_suitability: 'settings.weightModelSuitability',
  reviewer_diversity: 'settings.weightReviewerDiversity',
  session_continuity: 'settings.weightSessionContinuity',
}

/**
 * The share each weight would actually carry, since the router normalises the
 * set it is given.
 *
 * This is the number that governs behaviour: a slider reading 0.20 in a set
 * summing to 4.00 is a twentieth of the decision, not a fifth. Returns null
 * shares for a set that sums to zero or less, because there is no meaningful
 * share to state — and a zeroed set is not "everything equal".
 */
export function sharesOf(weights: Record<string, number>): Record<string, number | null> {
  const total = Object.values(weights).reduce((acc, v) => acc + v, 0)
  const out: Record<string, number | null> = {}
  for (const [k, v] of Object.entries(weights)) out[k] = total > 0 ? v / total : null
  return out
}

export function sumOf(weights: Record<string, number>): number {
  return Object.values(weights).reduce((acc, v) => acc + v, 0)
}

/** True when the set already sums to 1.00 within display precision, so the
 *  sliders mean what they say and no rescale is offered. */
export function isNormalised(sum: number): boolean {
  return Math.abs(sum - 1) < 0.005
}

/** Rescales a set to sum to 1.00 without changing any ratio between its
 *  members. A set summing to zero or less is returned untouched: there is no
 *  scale factor that turns it into a distribution. */
export function normalise(weights: Record<string, number>): Record<string, number> {
  const total = sumOf(weights)
  if (total <= 0) return weights
  const out: Record<string, number> = {}
  for (const [k, v] of Object.entries(weights)) out[k] = Number((v / total).toFixed(4))
  return out
}

/* ------------------------------------------------------------- Retention */

/** The value stored under [KEY_RETENTION] for "keep this forever". It is -1 and
 *  not 0 because 0 is a plausible typo meaning "delete immediately", and both
 *  classes it guards — recordings and the event archive — are ones where that
 *  mistake cannot be undone. */
export const RETAIN_INDEFINITELY = -1

/* The windows seed.RetentionPolicy actually carries. The form once offered
   `eventDays`, `recordingDays` and `artifactDays`, none of which is a field of
   the stored value — saving them would have added three keys nothing reads and
   left the real ones untouched.

   `auditDays` was removed on 2026-10-17 for the same reason, one layer up: it
   was a field of the stored value and of nothing else. Nothing has ever expired
   an audit record, so the box took a number, warned that records outside it
   could not be brought back, and did nothing with it. The audit trail is still
   named in this section — as a statement that it never expires, which is the
   fact the box was obscuring. See internal/seed/settings.go. */
export const RETENTION_FIELDS = [
  {
    field: 'domainEventDays',
    label: 'settings.retentionEvents',
    hint: 'settings.retentionEventsHint',
  },
  {
    field: 'terminalRecordingDays',
    label: 'settings.retentionRecordings',
    hint: 'settings.retentionRecordingsHint',
  },
] as const satisfies ReadonlyArray<{ field: string; label: MessageKey; hint: MessageKey }>

/** Parses a retention window. Returns null for anything that is not a whole
 *  number of days or the sentinel, so a half-typed value is reported rather
 *  than coerced into a policy. */
export function parseDays(raw: string): number | null {
  const cleaned = raw.trim()
  if (!/^-?\d+$/.test(cleaned)) return null
  const days = Number(cleaned)
  if (!Number.isFinite(days)) return null
  if (days === RETAIN_INDEFINITELY) return days
  return days >= 1 ? days : null
}

/* `startsExpiringTheAuditTrail` sat here and went with the field on 2026-10-17.
   It guarded the one change in this form that could not be undone, by warning
   before a window was put on a trail that had none — and the warning was the
   most misleading part of the control, because it bought an operator's consent
   for a deletion that was never implemented. This deployment stored
   `auditDays: 30` on that consent from 2026-08-15 until the field was removed. */

/* ------------------------------------------------------------------ Security */

/**
 * Whether the stored security policy requires a second factor for an L3 or L4
 * approval (ADR-0032).
 *
 * Anything but a stored `true` is off, which is the same reading `chiefDraftOf`
 * applies to the chief's loop and for the same reason: this is a gate, and a
 * gate that treats a malformed value as "on" locks out the one person who can
 * decide an approval, while one that treats it as "off" is the state the
 * deployment was already in.
 */
export function requiresMFAForApprovals(value: unknown): boolean {
  return asObject(value).requireMfaForHighRiskApprovals === true
}

/**
 * The document to write when the toggle moves.
 *
 * Built by spreading the stored value rather than from the one field, so a
 * control added to `seed.SecurityPolicy` later is carried through instead of
 * being deleted by whoever happens to save this one first.
 */
export function securityValueWith(value: unknown, required: boolean): Record<string, unknown> {
  return { ...asObject(value), requireMfaForHighRiskApprovals: required }
}

/* ------------------------------------------------------------------- Chief */

/**
 * The chief's designation, as its section edits it.
 *
 * Three decisions, and deliberately not four. `autonomousEveryMinutes` is on
 * the stored document and is *not* editable here: deciding on a timer is a
 * different question from choosing whose subscription answers, and it is not
 * one this console asks yet. What the editor must do — and what
 * [chiefValueOf] exists for — is carry the stored value through untouched, so
 * saving an account does not quietly switch off a loop somebody set.
 */
export interface ChiefDraft {
  accountProfileId: string
  modelId: string
  /** Whether the portfolio decision turn may run (ADR-0021). It does not gate
   *  the board's next-step options: designating an account is the permission to
   *  spend it, and the board spends one only when an operator presses. */
  enabled: boolean
}

export function chiefDraftOf(value: unknown): ChiefDraft {
  const o = asObject(value)
  return {
    accountProfileId: typeof o.accountProfileId === 'string' ? o.accountProfileId : '',
    modelId: typeof o.modelId === 'string' ? o.modelId : '',
    // Anything but a stored `true` is off. A missing flag is off, and a string
    // "false" left by an older writer is off too — the one reading of this that
    // cannot switch something on by accident.
    enabled: o.enabled === true,
  }
}

/** The fields whose draft differs from what the server holds. */
export function chiefChanged(draft: ChiefDraft, stored: ChiefDraft): Set<keyof ChiefDraft> {
  const out = new Set<keyof ChiefDraft>()
  for (const k of ['accountProfileId', 'modelId', 'enabled'] as const) {
    if (draft[k] !== stored[k]) out.add(k)
  }
  return out
}

export function chiefIsDirty(draft: ChiefDraft, stored: ChiefDraft): boolean {
  return chiefChanged(draft, stored).size > 0
}

/**
 * The draft, merged back over everything the server holds.
 *
 * An empty id is written as an *absent* field rather than as "": the stored
 * document is a Go struct with `omitempty`, and a designation cleared to the
 * empty string round-trips to the same zero id — but only the absent form
 * matches what the server would have written itself, and only it reads as "no
 * account" rather than as an account whose id is nothing.
 */
export function chiefValueOf(draft: ChiefDraft, stored: unknown): Record<string, unknown> {
  const out: Record<string, unknown> = { ...asObject(stored), enabled: draft.enabled }
  for (const k of ['accountProfileId', 'modelId'] as const) {
    if (draft[k]) out[k] = draft[k]
    else delete out[k]
  }
  return out
}

/** How often the chief decides on its own, in minutes. Zero — the shipped
 *  default — means no timer at all: it decides only when asked. */
export function autonomousMinutesOf(value: unknown): number {
  const n = asObject(value).autonomousEveryMinutes
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0
}
